"use client";

import { useEffect, useState } from "react";

import { usePathname } from "next/navigation";

import { canonicalBaseLanguageURLs } from "i18n/utils";

import { COMPANY_NAME } from "lib/constants";
import { countriesType } from "lib/types";

import AnimatedLink from "./animated-link";
import Logo from "./logo";

type LogoLinkProps = {
  locale: countriesType;
  logoVariant?: "pride" | "midsummer" | "regular" | "constitutionDay";
  translations: {
    goToStart: string;
    logo: string;
    midsummerLogoAlt: string;
    prideLogoAlt: string;
  };
};

export const LogoLink = ({
  locale,
  logoVariant,
  translations,
}: LogoLinkProps) => {
  const pathname = usePathname();
  const [isOnStartPage, setIsOnStartPage] = useState<boolean | null>(null);

  // Determine if we're on the start page after hydration to avoid mismatch
  useEffect(() => {
    const startPagePaths = ["/", "/en/", "/no/", "/da/"];
    setIsOnStartPage(startPagePaths.includes(pathname));
  }, [pathname]);

  // During SSR and initial render, always show the link to avoid hydration mismatch
  // After hydration, useEffect will update the state
  const shouldShowLink = isOnStartPage === false || isOnStartPage === null;

  return (
    <>
      {shouldShowLink ? (
        <AnimatedLink
          className="group"
          href={`${canonicalBaseLanguageURLs(locale)}`}
        >
          <Logo
            aria-label={`${COMPANY_NAME}, ${translations.goToStart}`}
            className="transition-none group-focus-visible:outline group-focus-visible:outline-8 group-focus-visible:outline-offset-4 group-focus-visible:outline-darkblue dark:group-focus-visible:outline-white"
            translations={translations}
            variant={logoVariant}
          />
        </AnimatedLink>
      ) : (
        <Logo translations={translations} variant={logoVariant} />
      )}
    </>
  );
};
